import { useEffect, useState } from 'react';
import { accounts } from '../backend';

/**
 * THE ADDRESS NAMES A COMPANY YOU CANNOT OPEN — #218, and the option Vinnie chose.
 *
 * WHY A PAGE AND NOT A REDIRECT. The rejected option put the person in their own
 * company with a banner explaining the link. It was the smaller build, and it
 * commits the fault this whole card is about: moving somebody somewhere they did
 * not choose. This page keeps its promise — you asked for TRINRE, so the page is
 * about TRINRE — and gives the one action that actually resolves it.
 *
 * IT NAMES THE COMPANY TO SOMEBODY WHO IS NOT IN IT, and that was decided rather
 * than overlooked. A stranger with a guessed address learns a company by that
 * name is on FlightDeck. Shown to Vinnie as a cost of this option and accepted,
 * because the alternative is a page that cannot explain itself — which is the
 * complaint that started the card: nobody could tell Richard anything.
 */
export default function CompanyNotYours({
  slug,
  me,
  mine,
  onLeave,
  onSignOut,
}: {
  slug: string;
  /** Shown so somebody signed in as the wrong account can see that at once. */
  me: string;
  mine: { id: string; name: string; slug: string; suspended: boolean }[];
  onLeave: (slug: string) => void;
  onSignOut: () => void;
}) {
  const [name, setName] = useState('');
  const [known, setKnown] = useState<boolean | null>(null);
  const [asking, setAsking] = useState(false);
  const [asked, setAsked] = useState<{ emailed: boolean; again: boolean } | null>(null);
  const [problem, setProblem] = useState('');

  useEffect(() => {
    let stop = false;
    /*
     * The company's NAME, so the page can say "TRINRE" rather than "trinre".
     * Through a function that runs as its owner, because the person reading
     * this is by definition not in the company and no ordinary read can answer.
     */
    accounts
      .companyAt(slug)
      .then((found: { name: string; here: boolean }) => {
        if (stop) return;
        setName(found.name);
        setKnown(found.here);
      })
      .catch(() => !stop && setKnown(false));
    return () => {
      stop = true;
    };
  }, [slug]);

  const ask = async () => {
    setAsking(true);
    setProblem('');
    try {
      setAsked(await accounts.askToJoin(slug));
    } catch (e) {
      // Said on the page. A request that appears to have been sent and was not
      // is worse than one that plainly failed.
      setProblem((e as Error).message || 'That could not be sent. Try again.');
    } finally {
      setAsking(false);
    }
  };

  /*
   * NOTHING WHILE IT IS STILL BEING LOOKED UP. A page that says "there is no
   * such company" and then corrects itself to name one is worse than a moment
   * of nothing.
   */
  if (known === null) return <div className="center-note">Loading…</div>;

  return (
    <TheAnswer
      slug={slug}
      name={name}
      known={known}
      me={me}
      mine={mine}
      asked={asked}
      asking={asking}
      problem={problem}
      onAsk={ask}
      onLeave={onLeave}
      onSignOut={onSignOut}
    />
  );
}

/**
 * THE PAGE ITSELF, SEPARATE FROM THE LOOKING-UP — and separate for the same
 * reason BrokenScreen is. Everything here waits on `companyAt`, so a screen
 * test renders "Loading…" and nothing else: the wording, and more importantly
 * WHICH ACTIONS DRAW, could not be checked by any honest route. That is exactly
 * how #270 survived — the state with no action at all was never rendered by
 * anything except a person unlucky enough to reach it.
 */
export function TheAnswer({
  slug,
  name,
  known,
  me,
  mine,
  asked = null,
  asking = false,
  problem = '',
  onAsk,
  onLeave,
  onSignOut,
}: {
  slug: string;
  name: string;
  known: boolean;
  me: string;
  mine: { id: string; name: string; slug: string; suspended: boolean }[];
  asked?: { emailed: boolean; again: boolean } | null;
  asking?: boolean;
  problem?: string;
  onAsk: () => void;
  onLeave: (slug: string) => void;
  onSignOut: () => void;
}) {

  const shown = name || slug;
  const others = mine.filter((w) => w.slug && w.slug !== slug);

  return (
    <div className="center-note company-refused">
      {known ? (
        <>
          <p>
            This address belongs to <strong>{shown}</strong>.
          </p>
          <p className="hint">
            You are not a member of {shown}, so there is nothing here for you to open.
          </p>
        </>
      ) : (
        <>
          <p>
            There is no company at <strong>/{slug}</strong>.
          </p>
          {/* The likeliest cause by far, and the one worth saying out loud:
              a link that was shortened, wrapped by a mail client, or typed. */}
          <p className="hint">Check the link — an address that has been shortened or retyped is the usual reason.</p>
        </>
      )}

      {/*
       * THE WAY OUT, AND IT DRAWS WHATEVER ELSE IS TRUE — #270.
       *
       * WHAT HAPPENED WITHOUT IT. This page told somebody which account they
       * were signed in as, knew that account was the wrong one, and offered
       * nothing to change it. Anybody with two addresses was stuck: clear the
       * browser's storage, or find another address to type. Vinnie, with a
       * screenshot of himself on TRINRE's address as admin@flightdeck.build:
       * *"there should be a log out button to change accounts if you have two
       * accounts with two different email addresses."*
       *
       * ⚠️ UNCONDITIONAL, WHICH IS THE HALF THAT WAS NOT REPORTED. Both other
       * actions on this page are conditional — the ask button needs the company
       * to exist, the list needs you to be in another one. Somebody following a
       * mistyped invitation link, on an account in no other company, was shown
       * a sentence and NOTHING ELSE: no button, no link, no way forward, on the
       * first screen they ever saw. This is the one thing here that is never
       * irrelevant, so it is the one thing that always draws.
       *
       * "SIGN IN AS SOMEBODY ELSE" RATHER THAN "SIGN OUT", which is what the
       * three sibling screens say. Those are about the account itself —
       * suspended, staff, refused — where leaving IS the point. Here the person
       * does not want to leave; they want to be their other self, and the label
       * should say what it is for rather than what it technically does.
       */}
      <p className="company-refused-who">
        {me && <>Signed in as {me} · </>}
        <button className="company-refused-swap" onClick={onSignOut}>
          Sign in as somebody else
        </button>
      </p>

      {asked ? (
        /*
         * WHAT ACTUALLY HAPPENED, IN THREE SENTENCES RATHER THAN ONE.
         *
         * This said "the administrators have been told" whatever came back, and
         * was caught saying it on a company that has no administrator listed —
         * nobody had been told anything. Somebody left waiting on an answer
         * that was never asked for is worse off than somebody told plainly that
         * they need to find another way round.
         */
        <p className={asked.emailed ? 'company-refused-sent' : 'company-refused-quiet'}>
          {asked.again
            ? `You have already asked ${shown}. They have not answered yet, and asking again does not tell them twice.`
            : asked.emailed
              ? `Asked. The administrators of ${shown} have been told who you are and what you were trying to open.`
              : `Your request has been recorded, but nobody at ${shown} could be emailed — there may be no administrator listed. You will need to reach them another way.`}
        </p>
      ) : (
        known && (
          <button className="btn btn-primary" onClick={onAsk} disabled={asking}>
            {asking ? 'Asking…' : `Ask ${shown} for access`}
          </button>
        )
      )}
      {problem && <div className="login-error">{problem}</div>}

      {others.length > 0 && (
        <div className="company-mine">
          <h4>Companies you are in</h4>
          {others.map((w) => (
            <button key={w.id} className="company-row" onClick={() => onLeave(w.slug)}>
              <span className="company-mark">{(w.name[0] ?? '?').toUpperCase()}</span>
              <span className="company-row-name">
                {w.name}
                {w.suspended && <span className="company-row-sub">Suspended</span>}
              </span>
              <span className="company-row-go">Open</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
